import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  AlertCircle,
  Info,
  Settings2,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/core/components/ui/card';
import { Label } from '@/core/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/core/components/ui/radio-group';
import { Badge } from '@/core/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { FileDropzone } from '../molecules/FileDropzone';
import { ImportPreview } from '../organisms/ImportPreview';
import { ImportProgressDialog } from '../organisms/ImportProgressDialog';
import { FileTypeSelector } from '../atoms/FileTypeSelector';
import { SheetSelector } from '../atoms/SheetSelector';
import { ImportLanguageSelector } from '../atoms/ImportLanguageSelector';
import { TargetLanguageSelector } from '../atoms/TargetLanguageSelector';
import { ApplicationTypeSelect } from '../atoms/ApplicationTypeSelect';
import { useImportStore, ImportStep } from '../../stores/import-store';
import { useTranslationStore } from '../../stores/translation-store';
import {
  ApplicationType,
  ApplicationTypeLabels,
  ImportMode,
  ImportModeLabels,
  ImportModeDescriptions,
  ImportFileType,
  JsonFormatTypeLabels,
  TranslationImportStatusResponse,
  ImportStatus,
} from '../../types/translation-types';
import { toast } from 'sonner';
import { cn } from '@/core/lib/utils';

const STEPS = [
  { step: ImportStep.Upload, label: 'Upload File' },
  { step: ImportStep.SelectSheet, label: 'Select Sheet', optional: true },
  { step: ImportStep.SelectLanguage, label: 'Select Language', optional: true },
  { step: ImportStep.Preview, label: 'Preview' },
  { step: ImportStep.Configure, label: 'Configure' },
  { step: ImportStep.Import, label: 'Import' },
  { step: ImportStep.Complete, label: 'Complete' },
];

// Custom order for import modes (AddNewOnly first as default)
const IMPORT_MODE_ORDER = [
  ImportMode.AddNewOnly,
  ImportMode.MergeUpdate,
  ImportMode.ReplaceAll,
];

export function BulkImportPage() {
  const navigate = useNavigate();

  // Use shared app type from translation store
  const { selectedAppType, setSelectedAppType } = useTranslationStore();

  const {
    currentStep,
    file,
    fileType,
    importMode,
    validationResult,
    importResult,
    isValidating,
    isImporting,
    isDownloadingTemplate,
    isExporting,
    isDetectingFormat,
    validationError,
    importError,
    detectedFormat,
    detectedSheets,
    detectedLanguages,
    detectedJsonFormat,
    requiresSheetSelection,
    requiresLanguageSelection,
    selectedSheet,
    selectedLanguageCode,
    targetLanguageCode,
    setCurrentStep,
    setFile,
    setFileType,
    setSelectedAppType: setImportSelectedAppType,
    setImportMode,
    setSelectedSheet,
    setSelectedLanguageCode,
    setTargetLanguageCode,
    detectFileFormat,
    validateFile,
    executeStreamingImport,
    cancelStreamingImport,
    downloadTemplate,
    exportTranslations,
    reset,
  } = useImportStore();

  // State for delete confirmation dialog
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // State for import progress dialog
  const [showProgressDialog, setShowProgressDialog] = useState(false);

  // State for SSE streaming import (both JSON and Excel files)
  const [streamingProgress, setStreamingProgress] = useState<TranslationImportStatusResponse | null>(null);
  const [isStreamingImport, setIsStreamingImport] = useState(false);
  const [streamingError, setStreamingError] = useState<string | null>(null);

  // Format duration from milliseconds to human-readable format
  const formatDuration = (ms: number): string => {
    if (ms < 1000) {
      return `${ms}ms`;
    }

    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      const remainingSeconds = seconds % 60;
      return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
    }

    if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    }

    return `${seconds}s`;
  };

  // Reset on mount and sync app type from shared store
  useEffect(() => {
    reset();
    // Sync the import store's selectedAppType with the shared one
    if (selectedAppType !== null) {
      setImportSelectedAppType(selectedAppType);
    }
  }, [reset, selectedAppType, setImportSelectedAppType]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      await downloadTemplate();
      toast.success('Template downloaded successfully');
    } catch {
      toast.error('Failed to download template');
    }
  }, [downloadTemplate]);

  const handleExport = useCallback(async () => {
    try {
      await exportTranslations();
      toast.success('Translations exported successfully');
    } catch {
      toast.error('Failed to export translations');
    }
  }, [exportTranslations]);

  const handleDetectFormat = useCallback(async () => {
    if (!selectedAppType) {
      toast.error('Please select a target application type');
      return;
    }
    await detectFileFormat();
  }, [detectFileFormat, selectedAppType]);

  const handleValidate = useCallback(async () => {
    await validateFile();
  }, [validateFile]);

  const handleImport = useCallback(async () => {
    // Use SSE streaming for both JSON and Excel imports
    setStreamingProgress(null);
    setStreamingError(null);
    setIsStreamingImport(true);
    setShowProgressDialog(true);

    const result = await executeStreamingImport((progress) => {
      setStreamingProgress(progress);
    });

    setIsStreamingImport(false);

    if (result) {
      // Streaming completed - onComplete will be called by ImportProgressDialog
      // when it detects the completed status
    } else {
      // Failed - error is already set in store
      setStreamingError(importError || 'Failed to import');
    }
  }, [executeStreamingImport, importError]);

  const handleImportComplete = useCallback((result: TranslationImportStatusResponse) => {
    // Close the progress dialog first
    setShowProgressDialog(false);

    if (result.status === ImportStatus.Completed) {
      // Import completed successfully
      const hasFailures = result.keysFailed > 0;

      // Build a meaningful message based on what actually changed
      const parts: string[] = [];
      if (result.keysAdded > 0) parts.push(`${result.keysAdded} keys added`);
      if (result.keysUpdated > 0) parts.push(`${result.keysUpdated} keys updated`);
      if (result.valuesUpdated > 0) parts.push(`${result.valuesUpdated} values updated`);
      if (result.keysDeleted > 0) parts.push(`${result.keysDeleted} keys deleted`);

      const changesMessage = parts.length > 0 ? parts.join(', ') : 'No changes made';

      if (hasFailures) {
        // Partial success
        toast.warning(`Import completed with ${result.keysFailed} failed keys. ${changesMessage}.`);
        setCurrentStep(ImportStep.Complete);
      } else {
        // Full success
        toast.success(`Import completed! ${changesMessage}.`);
        setCurrentStep(ImportStep.Complete);
      }
    } else if (result.status === ImportStatus.Failed) {
      // Import failed
      toast.error(result.errorMessage || 'Import failed');
    }
  }, [setCurrentStep]);

  const handleProgressDialogClose = useCallback(() => {
    setShowProgressDialog(false);
    // Reset streaming state
    setStreamingProgress(null);
    setStreamingError(null);
    setIsStreamingImport(false);
  }, []);

  const handleCancelStreamingImport = useCallback(() => {
    cancelStreamingImport();
    setIsStreamingImport(false);
    setShowProgressDialog(false);
    setStreamingProgress(null);
    setStreamingError(null);
    toast.info('Import cancelled');
  }, [cancelStreamingImport]);

  const handleBackToList = useCallback(() => {
    reset();
    navigate('/translations');
  }, [reset, navigate]);

  const handleStartOver = useCallback(() => {
    reset();
  }, [reset]);

  // Get visible steps based on current requirements
  const getVisibleSteps = () => {
    return STEPS.filter((s) => {
      if (s.step === ImportStep.SelectSheet) {
        return requiresSheetSelection;
      }
      if (s.step === ImportStep.SelectLanguage) {
        return requiresLanguageSelection;
      }
      return true;
    });
  };

  const renderStepIndicator = () => {
    const visibleSteps = getVisibleSteps();

    return (
      <div className="flex items-center justify-center mb-8 flex-wrap gap-y-2">
        {visibleSteps.map((s, index) => (
          <div key={s.step} className="flex items-center">
            <div
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium',
                currentStep === s.step && 'bg-primary text-primary-foreground',
                currentStep > s.step && 'bg-green-500 text-white',
                currentStep < s.step && 'bg-muted text-muted-foreground'
              )}
            >
              {currentStep > s.step ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                index + 1
              )}
            </div>
            <span
              className={cn(
                'ml-2 text-sm',
                currentStep >= s.step ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {s.label}
            </span>
            {index < visibleSteps.length - 1 && (
              <div
                className={cn(
                  'w-8 md:w-12 h-0.5 mx-2 md:mx-3',
                  currentStep > s.step ? 'bg-green-500' : 'bg-muted'
                )}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderUploadStep = () => {
    // Always accept both formats to allow auto-detection
    const acceptedFormats = '.xlsx,.xls,.json';

    const formatDescription = 'Excel (.xlsx, .xls) or JSON (.json) files up to 10MB';

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Translation File
          </CardTitle>
          <CardDescription>
            Upload an Excel or JSON file containing translations to import.
            Supports both new and legacy formats.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* File Type Selection */}
          <FileTypeSelector
            value={fileType}
            onChange={setFileType}
            disabled={isValidating || isDetectingFormat}
          />

          {/* App Type Selection */}
          <div className="space-y-2">
            <ApplicationTypeSelect
              value={selectedAppType}
              onChange={(appType) => {
                setSelectedAppType(appType);
                if (appType !== null) {
                  setImportSelectedAppType(appType);
                }
              }}
              className="w-[300px]"
              required
            />
            <p className="text-xs text-muted-foreground">
              Select the target application for these translations. For legacy Excel files with multiple sheets, the application type will be determined by the sheet selected.
            </p>
          </div>

          {/* Target Language Selection */}
          <TargetLanguageSelector
            value={targetLanguageCode}
            onChange={setTargetLanguageCode}
            disabled={isValidating || isDetectingFormat}
          />

          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={isDownloadingTemplate}
            >
              {isDownloadingTemplate ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Template
            </Button>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4 mr-2" />
              )}
              Export Current Translations
            </Button>
          </div>

          {/* File Dropzone */}
          <FileDropzone
            onFileSelect={setFile}
            selectedFile={file}
            accept={acceptedFormats}
            description={formatDescription}
            disabled={isValidating || isDetectingFormat}
          />

          {/* Detected Format Info */}
          {detectedFormat && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <p><strong>Detected Format:</strong> {detectedFormat}</p>
                  {detectedLanguages && detectedLanguages.length > 0 && (
                    <p>
                      <strong>Languages:</strong>{' '}
                      {detectedLanguages.map((lang) => (
                        <Badge key={lang} variant="secondary" className="mr-1">
                          {lang}
                        </Badge>
                      ))}
                    </p>
                  )}
                  {detectedJsonFormat && (
                    <p><strong>JSON Format:</strong> {JsonFormatTypeLabels[detectedJsonFormat]}</p>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Format Compatibility Success (JSON only) */}
          {!validationError && validationResult?.isValid && validationResult.isFormatCompatible === true && validationResult.expectedFormat && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Format Compatible:</strong> File format ({validationResult.expectedFormat}) matches the expected format for this application type.
              </AlertDescription>
            </Alert>
          )}

          {/* Format Compatibility Warning (JSON only) */}
          {!validationError && validationResult?.isFormatCompatible === false && validationResult.expectedFormat && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Format Mismatch:</strong> The file format ({validationResult.detectedFormat || 'Unknown'})
                does not match the expected format ({validationResult.expectedFormat}) for this application type.
                The import may fail or produce unexpected results.
              </AlertDescription>
            </Alert>
          )}

          {/* Validation Error (generic message) */}
          {validationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          {/* Validation Result with Errors (detailed) */}
          {validationResult && !validationResult.isValid && validationResult.errors && validationResult.errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium mb-2">Validation failed - errors found in the file:</p>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  {validationResult.errors.slice(0, 10).map((error, idx) => {
                    const rowNum = error.row ?? error.rowNumber;
                    const location = rowNum ? `Row ${rowNum}` : (error.columnName ? `Column: ${error.columnName}` : 'File');
                    return (
                      <li key={idx}>
                        <strong>{location}:</strong> {error.message}
                        {error.keyName && <span className="text-muted-foreground ml-1">(key: {error.keyName})</span>}
                      </li>
                    );
                  })}
                  {validationResult.errors.length > 10 && (
                    <li className="text-muted-foreground">
                      ...and {validationResult.errors.length - 10} more errors
                    </li>
                  )}
                </ul>
                <div className="mt-3 pt-3 border-t border-destructive/20">
                  <p className="text-sm mb-2">Fix the errors in your file, then re-select it to analyze again:</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Clear file and validation to allow re-selection
                      setFile(null);
                      useImportStore.setState({ validationResult: null, validationError: null });
                    }}
                    className="bg-background hover:bg-background/80"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Select Fixed File
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleBackToList}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Translations
            </Button>
            <Button
              onClick={handleDetectFormat}
              disabled={!file || !selectedAppType || isValidating || isDetectingFormat}
            >
              {(isValidating || isDetectingFormat) ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4 mr-2" />
              )}
              Analyze & Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderSelectSheetStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Select Excel Sheet</CardTitle>
        <CardDescription>
          This Excel file contains multiple sheets. Select which sheet to import.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {detectedSheets && (
          <SheetSelector
            sheets={detectedSheets}
            value={selectedSheet}
            onChange={setSelectedSheet}
            disabled={isValidating}
          />
        )}

        {/* Detected Format Info */}
        {detectedFormat && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Detected Format:</strong> {detectedFormat}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep(ImportStep.Upload)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleValidate}
            disabled={!selectedSheet || isValidating}
          >
            {isValidating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4 mr-2" />
            )}
            Validate & Preview
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderSelectLanguageStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Select Language</CardTitle>
        <CardDescription>
          This JSON file contains single-language translations.
          Please specify which language these translations belong to.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ImportLanguageSelector
          value={selectedLanguageCode}
          onChange={setSelectedLanguageCode}
          disabled={isValidating}
        />

        {/* Detected Format Info */}
        {detectedFormat && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p><strong>Detected Format:</strong> {detectedFormat}</p>
                {detectedJsonFormat && (
                  <p><strong>JSON Format:</strong> {JsonFormatTypeLabels[detectedJsonFormat]}</p>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep(ImportStep.Upload)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleValidate}
            disabled={!selectedLanguageCode || isValidating}
          >
            {isValidating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4 mr-2" />
            )}
            Validate & Preview
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderPreviewStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Import Preview</CardTitle>
        <CardDescription>
          Review the changes that will be made when you import this file
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Format Compatibility Warning (JSON only) */}
        {validationResult?.isFormatCompatible === false && validationResult.expectedFormat && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Format Mismatch:</strong> The file format ({validationResult.detectedFormat || 'Unknown'})
              does not match the expected format ({validationResult.expectedFormat}) for this application type.
              The import may fail or produce unexpected results.
            </AlertDescription>
          </Alert>
        )}

        {/* Format & Language Summary */}
        {(detectedFormat || detectedLanguages || validationResult?.isFormatCompatible !== undefined) && (
          <div className="border rounded-lg p-4 space-y-2 bg-muted/30">
            {detectedFormat && (
              <div className="flex justify-between text-sm items-center">
                <span className="text-muted-foreground">Detected Format:</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{detectedFormat}</span>
                  {validationResult?.isFormatCompatible === true && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  {validationResult?.isFormatCompatible === false && (
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              </div>
            )}
            {validationResult?.expectedFormat && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Expected Format:</span>
                <span className="font-medium">{validationResult.expectedFormat}</span>
              </div>
            )}
            {validationResult?.isFormatCompatible !== undefined && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Format Compatible:</span>
                <Badge variant={validationResult.isFormatCompatible ? "default" : "destructive"}>
                  {validationResult.isFormatCompatible ? "Yes" : "No"}
                </Badge>
              </div>
            )}
            {selectedSheet && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Selected Sheet:</span>
                <span className="font-medium">{selectedSheet}</span>
              </div>
            )}
            {selectedLanguageCode && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Language Code:</span>
                <span className="font-medium">{selectedLanguageCode}</span>
              </div>
            )}
            {targetLanguageCode && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Target Language:</span>
                <Badge variant="default" className="font-medium">
                  {targetLanguageCode}
                </Badge>
              </div>
            )}
            {detectedLanguages && detectedLanguages.length > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Languages Found:</span>
                <span className="font-medium">
                  {detectedLanguages.map((lang) => (
                    <Badge key={lang} variant="secondary" className="mr-1">
                      {lang}
                    </Badge>
                  ))}
                </span>
              </div>
            )}
          </div>
        )}

        {validationResult && (
          <ImportPreview validationResult={validationResult} />
        )}

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => {
            // Go back to appropriate step
            if (requiresLanguageSelection) {
              setCurrentStep(ImportStep.SelectLanguage);
            } else if (requiresSheetSelection) {
              setCurrentStep(ImportStep.SelectSheet);
            } else {
              setCurrentStep(ImportStep.Upload);
            }
          }}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={() => setCurrentStep(ImportStep.Configure)}
            disabled={!validationResult?.isValid}
          >
            Configure Import
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const handleProceedToImport = () => {
    if (importMode === ImportMode.ReplaceAll) {
      setShowDeleteConfirmation(true);
    } else {
      setCurrentStep(ImportStep.Import);
    }
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirmation(false);
    setCurrentStep(ImportStep.Import);
  };

  const renderConfigureStep = () => (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Configure Import</CardTitle>
          <CardDescription>
            Choose how existing translations should be handled
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <Label>Import Mode</Label>
            <RadioGroup
              value={importMode.toString()}
              onValueChange={(value) => setImportMode(parseInt(value) as ImportMode)}
              className="space-y-3"
            >
              {IMPORT_MODE_ORDER.map((mode) => (
                <div key={mode} className="flex items-start space-x-3">
                  <RadioGroupItem
                    value={mode.toString()}
                    id={`mode-${mode}`}
                    className="mt-1"
                  />
                  <div className="space-y-1">
                    <Label
                      htmlFor={`mode-${mode}`}
                      className="font-medium cursor-pointer"
                    >
                      {ImportModeLabels[mode]}
                      {mode === ImportMode.AddNewOnly && (
                        <Badge variant="secondary" className="ml-2 text-xs">Default</Badge>
                      )}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {ImportModeDescriptions[mode]}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          {importMode === ImportMode.ReplaceAll && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <strong>⚠️ Destructive Action Warning:</strong> Replace All mode will permanently delete all existing translations
                for this application type before importing. This action cannot be undone. You will be asked to confirm before proceeding.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(ImportStep.Preview)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button onClick={handleProceedToImport}>
              Review & Import
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>⚠️ Confirm Destructive Action</DialogTitle>
            <DialogDescription>
              You have selected <strong>Replace All</strong> mode, which will permanently delete all existing translations
              for <strong>{selectedAppType !== null ? ApplicationTypeLabels[selectedAppType] : 'Unknown'}</strong> before importing the new data.
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive" className="my-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>This action cannot be undone.</strong>
              <br />
              All current translations for this application will be permanently deleted.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
            >
              Yes, Delete All & Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  const renderImportStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Confirm Import</CardTitle>
        <CardDescription>
          Review your settings and start the import
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary */}
        <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Application:</span>
            <span className="font-medium">{selectedAppType !== null ? ApplicationTypeLabels[selectedAppType] : '—'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">File:</span>
            <span className="font-medium">{file?.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">File Type:</span>
            <span className="font-medium">{fileType === ImportFileType.Excel ? 'Excel' : 'JSON'}</span>
          </div>
          {detectedFormat && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Detected Format:</span>
              <span className="font-medium">{detectedFormat}</span>
            </div>
          )}
          {selectedSheet && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Selected Sheet:</span>
              <span className="font-medium">{selectedSheet}</span>
            </div>
          )}
          {selectedLanguageCode && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Language:</span>
              <span className="font-medium">{selectedLanguageCode}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Import Mode:</span>
            <span className="font-medium">{ImportModeLabels[importMode]}</span>
          </div>
          {validationResult && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Keys to Add:</span>
                <span className="font-medium text-green-600">{validationResult.keysToAdd}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Keys to Update:</span>
                <span className="font-medium text-blue-600">{validationResult.keysToUpdate}</span>
              </div>
            </>
          )}
        </div>

        {/* Import Result (if available, even with error) */}
        {importResult && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
            <h3 className="font-medium text-sm mb-2">Import Results:</h3>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Keys Added:</span>
              <span className="font-medium text-green-600">{importResult.keysAdded || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Keys Updated:</span>
              <span className="font-medium text-blue-600">{importResult.keysUpdated || 0}</span>
            </div>
            {importResult.keysDeleted !== undefined && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Keys Deleted:</span>
                <span className="font-medium text-red-600">{importResult.keysDeleted}</span>
              </div>
            )}
            {importResult.keysFailed !== undefined && importResult.keysFailed > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Keys Failed:</span>
                <span className="font-medium text-yellow-600">{importResult.keysFailed}</span>
              </div>
            )}
            {importResult.valuesUpdated !== undefined && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Values Updated:</span>
                <span className="font-medium text-blue-600">{importResult.valuesUpdated}</span>
              </div>
            )}
            {importResult.durationMs !== undefined && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Duration:</span>
                <span className="font-medium">{formatDuration(importResult.durationMs)}</span>
              </div>
            )}
          </div>
        )}

        {/* Failed Keys Table */}
        {importResult?.failedKeys && importResult.failedKeys.length > 0 && (
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <p className="font-medium mb-2">Failed Keys ({importResult.failedKeys.length}):</p>
              <div className="border rounded-lg overflow-hidden bg-white mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left p-2 font-medium">Key Name</th>
                      <th className="text-left p-2 font-medium w-20">Row</th>
                      <th className="text-left p-2 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.failedKeys.slice(0, 20).map((failedKey, idx) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="p-2 font-mono text-xs">{failedKey.keyName}</td>
                        <td className="p-2 text-muted-foreground">{failedKey.rowNumber || '-'}</td>
                        <td className="p-2 text-xs">{failedKey.errorMessage}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importResult.failedKeys.length > 20 && (
                  <div className="p-2 text-xs text-center border-t bg-muted/30">
                    Showing 20 of {importResult.failedKeys.length} failed keys
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Import Error */}
        {importError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-1">Import Failed</p>
              <p>{importError}</p>
              {importResult?.exception && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm">Show technical details</summary>
                  <pre className="mt-2 text-xs bg-destructive/10 p-2 rounded overflow-auto max-h-40">
                    {importResult.exception}
                  </pre>
                </details>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(ImportStep.Configure)}
            disabled={isImporting}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleImport} disabled={isImporting}>
            {isImporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Start Import
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderCompleteStep = () => {
    const hasFailures = importResult && importResult.keysFailed && importResult.keysFailed > 0;
    const isPartialSuccess = hasFailures && (importResult.keysAdded > 0 || importResult.keysUpdated > 0);
    const isFullSuccess = !hasFailures;

    return (
      <Card>
        <CardHeader className="text-center">
          <div className={cn(
            "mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center",
            isFullSuccess ? "bg-green-100" : "bg-yellow-100"
          )}>
            {isFullSuccess ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            )}
          </div>
          <CardTitle>
            {isFullSuccess ? 'Import Complete!' : 'Import Completed with Warnings'}
          </CardTitle>
          <CardDescription>
            {isFullSuccess
              ? 'Your translations have been successfully imported'
              : isPartialSuccess
              ? 'Some translations were imported successfully, but some keys failed'
              : 'Import completed but some keys could not be processed'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {importResult && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Keys Added:</span>
                <span className="font-medium text-green-600">{importResult.keysAdded || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Keys Updated:</span>
                <span className="font-medium text-blue-600">{importResult.keysUpdated || 0}</span>
              </div>
              {importResult.keysSkipped !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Keys Skipped:</span>
                  <span className="font-medium text-muted-foreground">{importResult.keysSkipped}</span>
                </div>
              )}
              {importResult.keysFailed !== undefined && importResult.keysFailed > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Keys Failed:</span>
                  <span className="font-medium text-yellow-600">{importResult.keysFailed}</span>
                </div>
              )}
              {importResult.keysDeleted !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Keys Deleted:</span>
                  <span className="font-medium text-red-600">{importResult.keysDeleted}</span>
                </div>
              )}
              {importResult.durationMs !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="font-medium">{importResult.durationMs}ms</span>
                </div>
              )}
            </div>
          )}

          {/* Failed Keys Table */}
          {importResult?.failedKeys && importResult.failedKeys.length > 0 && (
            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                <p className="font-medium mb-2">Failed Keys ({importResult.failedKeys.length}):</p>
                <div className="border rounded-lg overflow-hidden bg-white mt-2">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left p-2 font-medium">Key Name</th>
                        <th className="text-left p-2 font-medium w-20">Row</th>
                        <th className="text-left p-2 font-medium">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.failedKeys.slice(0, 20).map((failedKey, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="p-2 font-mono text-xs">{failedKey.keyName}</td>
                          <td className="p-2 text-muted-foreground">{failedKey.rowNumber || '-'}</td>
                          <td className="p-2 text-xs">{failedKey.errorMessage}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importResult.failedKeys.length > 20 && (
                    <div className="p-2 text-xs text-center border-t bg-muted/30">
                      Showing 20 of {importResult.failedKeys.length} failed keys
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {importResult?.errors && importResult.errors.length > 0 && (
            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                <p className="font-medium mb-2">Some rows had issues:</p>
                <ul className="list-disc pl-4 text-sm">
                  {importResult.errors.slice(0, 5).map((error, idx) => (
                    <li key={idx}>Row {error.row}: {error.message}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={handleStartOver}>
              Import Another File
            </Button>
            <Button onClick={handleBackToList}>
              Back to Translations
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case ImportStep.Upload:
        return renderUploadStep();
      case ImportStep.SelectSheet:
        return renderSelectSheetStep();
      case ImportStep.SelectLanguage:
        return renderSelectLanguageStep();
      case ImportStep.Preview:
        return renderPreviewStep();
      case ImportStep.Configure:
        return renderConfigureStep();
      case ImportStep.Import:
        return renderImportStep();
      case ImportStep.Complete:
        return renderCompleteStep();
      default:
        return renderUploadStep();
    }
  };

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Bulk Import Translations</h1>
        <Button variant="outline" onClick={() => navigate('/translations/import-config')}>
          <Settings2 className="h-4 w-4 mr-2" />
          Import Config
        </Button>
      </div>

      {renderStepIndicator()}
      {renderCurrentStep()}

      {/* Import Progress Dialog (SSE streaming mode) */}
      <ImportProgressDialog
        open={showProgressDialog}
        streamingStatus={streamingProgress}
        isStreaming={isStreamingImport}
        streamingError={streamingError}
        onClose={handleProgressDialogClose}
        onComplete={handleImportComplete}
        onCancel={handleCancelStreamingImport}
      />

      {/* Delete Confirmation Dialog (existing) */}
      <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete All Translations</DialogTitle>
            <DialogDescription>
              ⚠️ This action will permanently delete all existing translations for this application type
              before importing the new ones. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirmation(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowDeleteConfirmation(false);
                setCurrentStep(ImportStep.Import);
                handleImport();
              }}
            >
              Delete All & Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
